/**
 * Layer a user's admin-granted extras (users/{docId}.extra_features, served by
 * /users/me as `extraFeatures` / `extraBetaFeatures`) over their plan's feature map.
 * A grant only ever adds a feature — it never turns off one the plan includes.
 * Mirrors the API's merge for /api/extension/status so the web app and the
 * extension resolve the same answer.
 *
 * @param planFeatures - The plan's `features` or `betaFeatures` map.
 * @param extraFeatures - The user's extras map, if any.
 * @returns `planFeatures` itself when there are no grants, otherwise a new map; never mutates it.
 */
export const mergeExtraFeatures = <T extends object>(
  planFeatures: T,
  extraFeatures?: Record<string, boolean> | null,
): T => {
  const grants = Object.keys(extraFeatures ?? {}).filter((key) => extraFeatures?.[key] === true);
  // No grants (almost every user): keep the plan's own reference so effects keyed on
  // it — e.g. usePlanFeatureSync's extension push — don't re-fire on every refetch.
  if (grants.length === 0) return planFeatures;

  const merged = { ...planFeatures } as Record<string, unknown>;
  grants.forEach((key) => {
    merged[key] = true;
  });
  return merged as T;
};
